import { useState, useRef } from 'react'
import { FiX, FiChevronDown } from 'react-icons/fi'
import { type Opt, inputCls, norm } from './finance-shared'

// Combobox de categoria PRÓPRIO (não o <select> do navegador) para cada parte do
// rateio: fechado mostra o nome; focar abre a lista e digitar filtra. Categoria
// tem texto longo — por isso ocupa o espaço (flex-1) e o valor fica estreito.
//
// Morava dentro do TxnModal.tsx até 2026-07-30. Saiu para arquivo próprio
// porque o RateioCategoriaBox também precisa dele, e o TxnModal importa o
// RateioCategoriaBox — deixá-lo lá criaria import circular. Não pode ir para
// finance-shared.ts porque aquele arquivo é .ts e este componente tem JSX.
export function SplitCatSelect({ value, options, onChange, hint, fallbackName }: {
  value: string; options: Opt[]; onChange: (id: string) => void; hint?: string
  // Nome que a API já mandou junto com a parte (`category_name`). Serve de
  // rede: se por qualquer motivo a categoria não estiver na lista carregada
  // (lista ainda chegando, categoria fora do `kind` consultado, id que não
  // existe mais), o campo mostrava VAZIO e o operador via só o valor — como se
  // a parte não tivesse categoria. Preferir o dado que já veio pronto a
  // depender de um lookup que pode não casar.
  fallbackName?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  const selName = options.find(o => String(o.id) === value)?.name || (value ? (fallbackName || '') : '')
  const filtered = query.trim() ? options.filter(o => norm(o.name).includes(norm(query))) : options
  return (
    <div className="relative flex-1 min-w-0">
      <input ref={ref} value={open ? query : selName} autoComplete="off"
        placeholder={open ? 'Digite para filtrar…' : (hint || 'Sem categoria')}
        title="Clique e digite para filtrar as categorias"
        onFocus={() => { setQuery(''); setOpen(true) }}
        // onClick além do onFocus: os itens da lista usam onMouseDown+preventDefault
        // para o clique não perder o foco, então depois de escolher uma categoria o
        // input CONTINUA focado — e um novo clique não dispara onFocus. Sem isto o
        // campo ficava morto após a primeira escolha (e, como `value` é
        // `open ? query : selName`, digitar concatenava no nome já selecionado:
        // "Combustíveis" + "agua" = "Combustíveisagua"). O combobox de Categoria
        // principal já tinha as duas linhas; a extração de hoje era a hora de igualar.
        onClick={() => { if (!open) { setQuery(''); setOpen(true) } }}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false) } }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`${inputCls} h-9 pr-8`} /* PLANOS (21/10/2026): h-9 = mesma altura dos campos do TxnModal */ />
      {/* Mesmas metricas do combobox de Categoria (right-2, icone w-4): sao dois
          campos iguais na mesma tela, e seta menor e mais colada na borda lia
          como desalinhada em relacao a de cima. A seta gira ao abrir — sem isso
          nada no controle fechado diz que ele abre. */}
      <button type="button" onMouseDown={e => e.preventDefault()}
        onClick={() => { if (value) { onChange(''); setQuery(''); ref.current?.focus() } else if (open) setOpen(false); else ref.current?.focus() }}
        title={value ? 'Limpar categoria' : 'Abrir lista'}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground">
        {value ? <FiX className="w-4 h-4" />
               : <FiChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && (
        <ul className="neu-pop absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg glass-popover">
          <li>
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(''); setOpen(false) }}
              className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-secondary">Sem categoria</button>
          </li>
          {filtered.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">Nenhuma categoria encontrada</li>}
          {filtered.map(c => (
            <li key={c.id}>
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(String(c.id)); setOpen(false) }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary ${String(c.id) === value ? 'text-primary font-medium' : 'text-foreground'}`}>
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
